import React, { FC, useEffect, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { useFieldArray, useForm } from 'react-hook-form'

import { AssignProjectListTopTitle } from 'src/components/molecules'
import { UserBulkAssign } from 'src/components/organisms'
import { UserAssignProjectsSearchArea } from 'src/components/organisms/users/UserAssignProjectsSearchArea'

import { BaseYup, yup, yupResolver } from 'src/customYup'
import { useGetProjects, useMasterData, useProject, useToast, useUser } from 'src/hooks'
import {
  AssignedProjectEntity,
  AssignedProjectSearchContentEntity,
  UserAssignProjectFormEntity,
  UserEntity,
} from 'src/types'
import { CompassRoleEntity } from 'src/types/entities/CompassRole'

const schema: yup.SchemaOf<UserAssignProjectFormEntity> = BaseYup.object({
  projects: BaseYup.array().of(
    BaseYup.object({
      projectId: BaseYup.string().required(),
      projectName: BaseYup.string().required(),
      projectStatusName: BaseYup.string().required(),
      projectStatusDisplayName: BaseYup.string().required(),
      // デフォルト値を設定しなく（undefined）なり、更新前に削除（isDeleting = true）を行った上で更新を実行出来る必要があるため、required() は設定しない
      compassRoleId: BaseYup.number(),
      compassRolePriority: BaseYup.number(),
      isAlreadyAssigned: BaseYup.boolean().required(),
      isSelected: BaseYup.boolean().optional(),
      isDeleting: BaseYup.boolean().optional(),
      isFiltering: BaseYup.boolean().optional(),
    })
  ),
  isSendAssignEmail: BaseYup.boolean().required(),
})

type Props = {
  user: UserEntity
  projects: AssignedProjectEntity[]
  compassRoles: CompassRoleEntity[]
  searchContent: AssignedProjectSearchContentEntity | null
  editButtonId: string
  cancelButtonId: string
  onUpdateSearchContent: (searchContent: AssignedProjectSearchContentEntity | null) => void
  onToNormalMode: () => void
  className?: string
}

export const EditUserAssignProjectsContent: FC<Props> = ({
  user,
  projects,
  compassRoles,
  searchContent,
  editButtonId,
  cancelButtonId,
  onUpdateSearchContent,
  onToNormalMode,
  className,
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const { assignUserProjects } = useUser()
  const { displayErrorTextToast } = useToast()
  const { masterData } = useMasterData()
  const { projects: allProjects } = useGetProjects()
  const { getIsProjectMatch } = useProject()

  const client = useApolloClient()

  const toFormEntity = (assignProjects: AssignedProjectEntity[]): UserAssignProjectFormEntity => {
    return {
      projects: assignProjects
        .filter(p => !p.isDeleting)
        .map(project => ({
          projectId: project.id,
          projectName: project.name,
          projectStatusName: project.status.name,
          projectStatusDisplayName: project.status.displayName,
          compassRoleId: project.compassRole.id,
          compassRolePriority: project.compassRole.priority,
          isAlreadyAssigned: true,
          isFiltering: searchContent ? !getIsProjectMatch(project, searchContent) : false,
        })),
      isSendAssignEmail: false,
    }
  }

  const {
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<UserAssignProjectFormEntity>({
    resolver: yupResolver(schema),
    defaultValues: toFormEntity(projects),
  })

  const { prepend, update, remove } = useFieldArray({ control, name: 'projects' })

  const onSubmit = async (formEntity: UserAssignProjectFormEntity) => {
    setIsLoading(true)

    const errorProjects = formEntity.projects.filter((project, index) => {
      // 削除していない、かつCompassRoleの設定がされていない場合はエラーとする
      if (!project.isDeleting && !project.compassRoleId) {
        setError(`projects.${index}.compassRoleId`, { message: '選択ください' })
        return true
      }
      return false
    })

    if (errorProjects.length > 0) {
      setIsLoading(false)
      return
    }

    if (!masterData || compassRoles.length === 0) {
      displayErrorTextToast('システム内部のエラーが起きました。再度お試し下さい')
      setIsLoading(false)
      return
    }

    try {
      await assignUserProjects({
        userId: user.id,
        isExternal: user.isExternal,
        formEntity,
        compassRoles,
      })

      onToNormalMode()
    } catch {
      displayErrorTextToast('プロジェクトのアサインに失敗しました')
    } finally {
      setIsLoading(false)
    }

    try {
      // プロジェクト一覧・詳細など広範囲に及ぶ処理のためキャッシュをリセットする。リセットを行うと自動的に画面に必要なqueryが再度実行される
      await client.resetStore()
    } catch {
      // パラメータが足りずにエラーになることがあるが、キャッシュのリセットは成功しているので特に何もしない
      // 特に何もしない
    }
  }

  const handleOnCancel = () => {
    onToNormalMode()
    reset()
  }

  useEffect(() => {
    // 検索条件が変わったタイミングで更新
    // 検索条件にマッチしていない場合はフィルタリングフラグを立てる
    const updatedProjects = watch('projects').map(project => {
      // 対象となるAssignedProjectEntityを取得
      const targetProject = projects.find(p => p.id === project.projectId)

      if (!targetProject) {
        return project
      }

      // compass権限はformで選択している値に更新する
      const compassRole = compassRoles.find(role => role.id === project.compassRoleId)
      const updatedProject: AssignedProjectEntity = {
        ...targetProject,
        compassRole: {
          id: compassRole?.id ?? targetProject.compassRole.id,
          name: compassRole?.name ?? targetProject.compassRole.name,
          displayName: compassRole?.displayName ?? targetProject.compassRole.displayName,
          priority: compassRole?.priority ?? targetProject.compassRole.priority,
        },
      }

      return {
        ...project,
        // 検索条件にマッチしていない場合はフィルタリングフラグを立てる
        isFiltering: searchContent ? !getIsProjectMatch(updatedProject, searchContent) : false,
      }
    })
    setValue('projects', updatedProjects)

    // 以下2つ以外を指定すると無限ループになるため指定しない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchContent, projects])

  const assignedProjects = watch('projects').filter(project => !project.isDeleting)

  return (
    <div className={className}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <AssignProjectListTopTitle
          isEditMode
          editButtonId={editButtonId}
          cancelButtonId={cancelButtonId}
          onClickCancel={handleOnCancel}
          isLoading={isLoading}
        />
        <UserAssignProjectsSearchArea
          editButtonId={editButtonId}
          cancelButtonId={cancelButtonId}
          searchContent={searchContent}
          allAssignedProjectCount={assignedProjects.length}
          displayedProjectStatuses={assignedProjects
            .filter(project => !project.isFiltering)
            .map(p => ({ name: p.projectStatusName }))}
          onUpdateSearchContent={onUpdateSearchContent}
          className="tw-mt-s1"
        />
        <UserBulkAssign
          watch={watch}
          prepend={prepend}
          update={update}
          setValue={setValue}
          formErrors={errors}
          remove={remove}
          projects={allProjects}
          className="tw-mt-s1"
        />
      </form>
    </div>
  )
}
